import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  Plus, 
  Copy, 
  Trash2, 
  Ban, 
  Clock, 
  Users, 
  Loader2,
  MoreHorizontal,
  RefreshCcw,
  Play,
  Pause,
  Eye,
  Settings2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Header from "./Header";
import AdminSidebar from "./Sidebar";
import { db, auth } from "@/firebase/config";
import { collection, addDoc, query, onSnapshot, doc, updateDoc, deleteDoc, where, getDocs } from "firebase/firestore";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { useToast } from "@/components/ui/use-toast";

interface RegistrationLink {
  id: string;
  code: string;
  name: string; // Friendly name e.g. "Term 1 Volunteers"
  passcode: string; // Added passcode
  maxUses: number;
  usedCount: number;
  expiresAt: string; // ISO string
  createdAt: string;
  createdBy: string;
  schoolID: string;
  status: 'active' | 'revoked' | 'expired';
}

interface VolunteerSessionRecord {
  id: string;
  volunteerName: string;
  linkId: string;
  linkName: string;
  schoolID: string;
  status: 'active' | 'paused' | 'revoked';
  createdAt: string;
  studentCount?: number;
}

const RegistrationManagement = () => {
    const navigate = useNavigate();
    const { toast } = useToast();
    
    // State
    const [loading, setLoading] = useState(true);
    const [links, setLinks] = useState<RegistrationLink[]>([]);
    const [schoolID, setSchoolID] = useState<string | null>(null);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    
    // Volunteer Management State
    const [currentView, setCurrentView] = useState<'links' | 'volunteers'>('links');
    const [selectedLink, setSelectedLink] = useState<RegistrationLink | null>(null);
    const [volunteers, setVolunteers] = useState<VolunteerSessionRecord[]>([]);
    const [loadingVolunteers, setLoadingVolunteers] = useState(false);
    
    // Form State
    const [linkName, setLinkName] = useState("");
    const [passcode, setPasscode] = useState(""); // Added state
    const [maxVolunteers, setMaxVolunteers] = useState("10");
    const [validityHours, setValidityHours] = useState("72");
    const [creating, setCreating] = useState(false);

    // Auth & Data Fetching
    useEffect(() => {
        const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
            if (user) {
                try {
                    const token = await getIdTokenResult(user);
                    const claims = token.claims;
                    
                    const aff = (claims.affiliations as any[]) || [];
                    const adminAff = aff.find(a => a.role === 'admin' && a.schoolID) || claims.defaultAffiliation;
                    
                    if (adminAff?.schoolID) {
                        setSchoolID(adminAff.schoolID);
                    } else {
                        setLoading(false); 
                    }
                } catch (e) {
                    console.error(e);
                    setLoading(false);
                }
            } else {
                 setLoading(false);
            }
        });
        return () => unsubscribeAuth();
    }, []);

    useEffect(() => {
        if (!schoolID) return;

        // Query global 'volunteerLinks' where schoolID matches
        const q = query(
            collection(db, 'volunteerLinks'), 
            where('schoolID', '==', schoolID)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedLinks: RegistrationLink[] = [];
            const now = new Date();

            snapshot.forEach(doc => {
                const data = doc.data();
                let status = data.status || 'active';
                
                // Auto-expire check
                if (status === 'active' && new Date(data.expiresAt) < now) {
                    status = 'expired';
                }

                fetchedLinks.push({
                    id: doc.id,
                    code: data.code,
                    name: data.name,
                    passcode: data.passcode || 'N/A', // Handle legacy
                    maxUses: data.maxUses,
                    usedCount: data.usedCount || 0,
                    expiresAt: data.expiresAt,
                    createdAt: data.createdAt,
                    createdBy: data.createdBy,
                    schoolID: data.schoolID,
                    status: status
                });
            });
            // Client-side sort by createdAt desc
            fetchedLinks.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
            
            setLinks(fetchedLinks);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching links:", error);
            toast({ 
                title: "Error loading links", 
                description: "Could not fetch registration links. Please check your connection.",
                variant: "destructive"
            });
            setLoading(false);
        });

        return () => unsubscribe();
    }, [schoolID]);

    const generateCode = () => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; 
        let result = '';
        for (let i = 0; i < 9; i++) {
            if (i === 3 || i === 6) result += '-';
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return `INV-${result}`; 
    };

    const generatePasscode = () => {
        return Math.floor(100000 + Math.random() * 900000).toString();
    };

    const openCreateDialog = () => {
        setPasscode(generatePasscode());
        setShowCreateDialog(true);
    };

    const handleCreateLink = async () => {
        if (!schoolID || !auth.currentUser) return;
        if (!linkName) {
             toast({ title: "Name required", description: "Please name this invitation link", variant: "destructive" });
             return;
        }
        if (!passcode || passcode.length < 4) {
            toast({ title: "Passcode required", description: "Please set a secure passcode (min 4 chars)", variant: "destructive" });
            return;
       }

        setCreating(true);
        try {
            const code = generateCode();
            const now = new Date();
            const expiry = new Date(now.getTime() + parseInt(validityHours) * 60 * 60 * 1000);

            const newLink = {
                code,
                name: linkName,
                passcode,
                maxUses: parseInt(maxVolunteers),
                usedCount: 0,
                expiresAt: expiry.toISOString(),
                createdAt: now.toISOString(),
                createdBy: auth.currentUser.uid,
                schoolID: schoolID,
                status: 'active'
            };

            await addDoc(collection(db, 'volunteerLinks'), newLink);
            
            toast({ title: "Invitation Created", description: "Link generated successfully." });
            setShowCreateDialog(false);
            setLinkName("");
            setMaxVolunteers("10");
            setValidityHours("72");
            setPasscode("");
            
        } catch (error) {
            console.error(error);
            toast({ title: "Error", description: "Failed to create link.", variant: "destructive" });
        } finally {
            setCreating(false);
        }
    };

    const handleRevoke = async (linkId: string) => {
        try {
            await updateDoc(doc(db, 'volunteerLinks', linkId), {
                status: 'revoked'
            });
            toast({ title: "Link Revoked", description: "This link can no longer be used." });
        } catch (error) {
            toast({ title: "Error", description: "Failed to revoke link.", variant: "destructive" });
        }
    };

    const handleDelete = async (linkId: string) => {
        if (!confirm("Are you sure? Note: This will not kick out currently logged in volunteers, but will delete the record.")) return;
         try {
            await deleteDoc(doc(db, 'volunteerLinks', linkId));
            toast({ title: "Link Deleted" });
        } catch (error) {
            toast({ title: "Error", description: "Failed to delete link.", variant: "destructive" });
        }
    };

    const copyLink = (code: string) => {
        const url = `${window.location.origin}/volunteer-access/${code}`;
        navigator.clipboard.writeText(url);
        toast({ title: "Copied!", description: "Link copied to clipboard." });
    };

    const copyPasscode = (passcode: string) => {
        navigator.clipboard.writeText(passcode);
        toast({ title: "Copied!", description: "Passcode copied to clipboard." });
    };

    // Volunteer Management Functions
    const viewVolunteers = async (link: RegistrationLink) => {
        setSelectedLink(link);
        setCurrentView('volunteers');
        setLoadingVolunteers(true);
        
        try {
            const q = query(
                collection(db, 'volunteerSessions'), 
                where('linkId', '==', link.id)
            );
            const snapshot = await getDocs(q);
            
            const volunteerList: VolunteerSessionRecord[] = [];
            
            for (const docSnap of snapshot.docs) {
                const data = docSnap.data();
                
                // Count students registered by this volunteer
                const studentQuery = query(
                    collection(db, `schools/${link.schoolID}/students`),
                    where('submittedBy', '==', `Volunteer: ${data.volunteerName}`)
                );
                const studentSnapshot = await getDocs(studentQuery);
                
                volunteerList.push({
                    id: docSnap.id,
                    volunteerName: data.volunteerName,
                    linkId: data.linkId,
                    linkName: data.linkName || link.name,
                    schoolID: data.schoolID,
                    status: data.status || 'active',
                    createdAt: data.createdAt,
                    studentCount: studentSnapshot.size
                });
            }
            
            // Sort by createdAt desc
            volunteerList.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
            
            setVolunteers(volunteerList);
        } catch (error) {
            console.error("Error fetching volunteers:", error);
            toast({ title: "Error", description: "Failed to load volunteers.", variant: "destructive" });
        } finally {
            setLoadingVolunteers(false);
        }
    };

    const handlePauseVolunteer = async (volunteerId: string) => {
        try {
            await updateDoc(doc(db, 'volunteerSessions', volunteerId), {
                status: 'paused'
            });
            // Update local state
            setVolunteers(prev => prev.map(v => 
                v.id === volunteerId ? { ...v, status: 'paused' as const } : v
            ));
            toast({ title: "Volunteer Paused", description: "They can no longer register new students." });
        } catch (error) {
            toast({ title: "Error", description: "Failed to pause volunteer.", variant: "destructive" });
        }
    };

    const handleResumeVolunteer = async (volunteerId: string) => {
        try {
            await updateDoc(doc(db, 'volunteerSessions', volunteerId), {
                status: 'active'
            });
            setVolunteers(prev => prev.map(v => 
                v.id === volunteerId ? { ...v, status: 'active' as const } : v
            ));
            toast({ title: "Volunteer Resumed", description: "They can now register students again." });
        } catch (error) {
            toast({ title: "Error", description: "Failed to resume volunteer.", variant: "destructive" });
        }
    };

    const handleRevokeVolunteer = async (volunteerId: string, volunteerName: string) => {
        if (!confirm(`Revoke access for ${volunteerName}? They will be logged out immediately.`)) return;
        
        try {
            await updateDoc(doc(db, 'volunteerSessions', volunteerId), {
                status: 'revoked'
            });
            setVolunteers(prev => prev.map(v => 
                v.id === volunteerId ? { ...v, status: 'revoked' as const } : v
            ));
            toast({ title: "Volunteer Revoked", description: `${volunteerName} has been removed.` });
        } catch (error) {
            toast({ title: "Error", description: "Failed to revoke volunteer.", variant: "destructive" });
        }
    };

    // Volunteers View - Full Page
    if (currentView === 'volunteers' && selectedLink) {
        return (
            <SidebarProvider>
                <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
                    <AdminSidebar onSectionChange={() => {}} />
                    <div className="flex flex-col flex-1 h-full">
                        <Header />
                        <main className="flex-1 overflow-y-auto p-3 sm:p-6">
                            <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6">
                                
                                {/* Top Bar */}
                                <div className="flex flex-col gap-4 mb-6">
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                        <div className="flex items-center gap-3">
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                className="h-8 w-8 p-0 sm:h-auto sm:w-auto sm:px-3 sm:py-2" 
                                                onClick={() => setCurrentView('links')}
                                            >
                                                <ArrowLeft className="h-4 w-4 sm:mr-2" />
                                                <span className="hidden sm:inline">Back to Links</span>
                                            </Button>
                                            <div>
                                                <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                                                    Volunteers
                                                </h1>
                                                <p className="text-xs sm:text-sm text-gray-500 line-clamp-1">
                                                    {selectedLink.name}
                                                </p>
                                            </div>
                                        </div>
                                        <Button variant="outline" size="sm" onClick={() => viewVolunteers(selectedLink)} className="w-full sm:w-auto">
                                            <RefreshCcw className="h-4 w-4 mr-2" /> Refresh
                                        </Button>
                                    </div>
                                    
                                    <Card className="bg-slate-50 dark:bg-slate-800/50 border-none shadow-none">
                                        <CardContent className="p-3 sm:p-4">
                                            <div className="grid grid-cols-2 gap-4 text-xs sm:text-sm">
                                                <div>
                                                    <p className="text-gray-500 mb-1">Link Code</p>
                                                    <code className="bg-white dark:bg-slate-900 px-2 py-1 rounded font-mono block truncate">{selectedLink.code}</code>
                                                </div>
                                                <div>
                                                    <p className="text-gray-500 mb-1">Passcode</p>
                                                    <code className="bg-white dark:bg-slate-900 px-2 py-1 rounded font-mono block truncate">{selectedLink.passcode}</code>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </div>

                                {/* Volunteers Card/List */}
                                <div className="space-y-4">
                                    <div className="hidden sm:flex items-center gap-2 mb-2">
                                        <Users className="h-5 w-5 text-gray-500" />
                                        <h2 className="text-lg font-semibold">Active Sessions</h2>
                                    </div>
                                        <div className="space-y-4">
                                            {/* Mobile View: Cards */}
                                            <div className="grid grid-cols-1 gap-4 sm:hidden">
                                                {volunteers.map((v) => (
                                                    <Card key={v.id} className="overflow-hidden border-slate-200 dark:border-slate-800">
                                                        <CardContent className="p-4">
                                                            <div className="flex justify-between items-start mb-3">
                                                                <div>
                                                                    <h3 className="font-bold text-slate-900 dark:text-white uppercase tracking-tight">{v.volunteerName}</h3>
                                                                    <p className="text-[10px] text-slate-500 font-mono mt-0.5">{new Date(v.createdAt).toLocaleDateString()}</p>
                                                                </div>
                                                                {v.status === 'active' && <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-none text-[10px]">Active</Badge>}
                                                                {v.status === 'paused' && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-none text-[10px]">Paused</Badge>}
                                                                {v.status === 'revoked' && <Badge variant="destructive" className="text-[10px]">Revoked</Badge>}
                                                            </div>
                                                            
                                                            <div className="flex items-center gap-2 mb-4">
                                                                <div className="bg-slate-100 dark:bg-slate-800 px-3 py-2 rounded-lg flex-1">
                                                                    <p className="text-[9px] text-slate-400 uppercase font-bold">Students Registered</p>
                                                                    <p className="text-lg font-black text-slate-900 dark:text-white">{v.studentCount || 0}</p>
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="flex gap-2">
                                                                {v.status === 'active' && (
                                                                    <Button size="sm" variant="outline" onClick={() => handlePauseVolunteer(v.id)} className="flex-1 text-xs h-9">
                                                                        <Pause className="h-3 w-3 mr-1" /> Pause
                                                                    </Button>
                                                                )}
                                                                {v.status === 'paused' && (
                                                                    <Button size="sm" variant="outline" onClick={() => handleResumeVolunteer(v.id)} className="flex-1 text-xs h-9 text-green-600 border-green-200">
                                                                        <Play className="h-3 w-3 mr-1" /> Resume
                                                                    </Button>
                                                                )}
                                                                {v.status !== 'revoked' && (
                                                                    <Button size="sm" variant="outline" onClick={() => handleRevokeVolunteer(v.id, v.volunteerName)} className="flex-1 text-xs h-9 text-red-600 border-red-200">
                                                                        <Ban className="h-3 w-3 mr-1" /> Revoke
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </CardContent>
                                                    </Card>
                                                ))}
                                            </div>

                                            {/* Desktop View: Table */}
                                            <div className="hidden sm:block overflow-hidden border rounded-lg bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                                                <Table>
                                                    <TableHeader>
                                                        <TableRow>
                                                            <TableHead>Volunteer Name</TableHead>
                                                            <TableHead>Status</TableHead>
                                                            <TableHead>Students Registered</TableHead>
                                                            <TableHead>Joined At</TableHead>
                                                            <TableHead className="text-right">Actions</TableHead>
                                                        </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {volunteers.map((v) => (
                                                            <TableRow key={v.id}>
                                                                <TableCell className="font-medium">{v.volunteerName}</TableCell>
                                                                <TableCell>
                                                                    {v.status === 'active' && <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-none">Active</Badge>}
                                                                    {v.status === 'paused' && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 border-none">Paused</Badge>}
                                                                    {v.status === 'revoked' && <Badge variant="destructive">Revoked</Badge>}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <span className="font-bold text-lg">{v.studentCount || 0}</span>
                                                                    <span className="text-muted-foreground ml-1">students</span>
                                                                </TableCell>
                                                                <TableCell className="text-muted-foreground">
                                                                    {new Date(v.createdAt).toLocaleDateString()}
                                                                </TableCell>
                                                                <TableCell className="text-right">
                                                                    <div className="flex justify-end gap-2">
                                                                        {v.status === 'active' && (
                                                                            <Button size="sm" variant="outline" onClick={() => handlePauseVolunteer(v.id)} title="Pause Session">
                                                                                <Pause className="h-3.5 w-3.5" />
                                                                            </Button>
                                                                        )}
                                                                        {v.status === 'paused' && (
                                                                            <Button size="sm" variant="outline" onClick={() => handleResumeVolunteer(v.id)} className="text-green-600 border-green-200 hover:bg-green-50" title="Resume Session">
                                                                                <Play className="h-3.5 w-3.5" />
                                                                            </Button>
                                                                        )}
                                                                        {v.status !== 'revoked' && (
                                                                            <Button size="sm" variant="outline" onClick={() => handleRevokeVolunteer(v.id, v.volunteerName)} className="text-red-600 border-red-200 hover:bg-red-50" title="Revoke Access">
                                                                                <Ban className="h-3.5 w-3.5" />
                                                                            </Button>
                                                                        )}
                                                                    </div>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </main>
                        </div>
                    </div>
                </SidebarProvider>
            );
        }

    // Default Links View
    return (
        <SidebarProvider>
            <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
                <AdminSidebar onSectionChange={() => {}} />
                <div className="flex flex-col flex-1 h-full overflow-hidden">
                    <Header />
                    <div className="hidden" aria-hidden="true" /> {/* Dummy to fix flex issues if any */}
                    <main className="flex-1 overflow-y-auto p-3 sm:p-6">
                         <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6">
                            
                            {/* Top Bar */}
                            <div className="flex flex-col gap-4 mb-2">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div className="flex items-center gap-3">
                                        <Button 
                                            variant="outline" 
                                            size="sm"
                                            className="h-8 w-8 p-0 sm:h-auto sm:w-auto sm:px-3 sm:py-2 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800" 
                                            onClick={() => navigate('/dashboard/settings')}
                                        >
                                            <ArrowLeft className="h-4 w-4 sm:mr-2" />
                                            <span className="hidden sm:inline">Back</span>
                                        </Button>
                                        <div>
                                            <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100 italic">
                                                Invites
                                            </h1>
                                            <p className="text-[10px] sm:text-xs text-slate-500 uppercase font-black tracking-widest leading-none">Registration Mgmt</p>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <Button 
                                            variant="outline" 
                                            size="sm"
                                            onClick={() => navigate('/dashboard/admission-setup')}
                                            className="h-10 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
                                        >
                                            <Settings2 className="h-4 w-4 mr-2" />
                                            Admission Number Setup
                                        </Button>
                                        <Button 
                                            size="sm"
                                            onClick={openCreateDialog}
                                            className="h-10 bg-orange-600 hover:bg-orange-700 text-white shadow-lg shadow-orange-900/20"
                                        >
                                            <Plus className="h-4 w-4 mr-2" />
                                            New Link
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* Create Dialog */}
                            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>Create Volunteer Invite Link</DialogTitle>
                                        <DialogDescription>
                                            Generate a protected link for volunteers.
                                        </DialogDescription>
                                    </DialogHeader>
                                    <div className="space-y-4 py-4">
                                        <div className="space-y-2">
                                            <Label>Link Name (Reference)</Label>
                                            <Input 
                                                placeholder="e.g. Jan 2026 Registration Drive" 
                                                value={linkName}
                                                onChange={e => setLinkName(e.target.value)}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Access Passcode</Label>
                                            <div className="flex gap-2">
                                                <Input 
                                                    value={passcode}
                                                    onChange={e => setPasscode(e.target.value)}
                                                    className="font-mono tracking-widest"
                                                />
                                                <Button variant="outline" size="icon" onClick={() => setPasscode(generatePasscode())} title="Regenerate">
                                                    <RefreshCcw className="h-4 w-4" />
                                                </Button>
                                            </div>
                                            <p className="text-xs text-muted-foreground">Volunteers must enter this code to join.</p>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>Max Volunteers</Label>
                                                <Input 
                                                    type="number" 
                                                    min="1"
                                                    value={maxVolunteers}
                                                    onChange={e => setMaxVolunteers(e.target.value)}
                                                />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Validity</Label>
                                                <Select value={validityHours} onValueChange={setValidityHours}>
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="24">24 Hours</SelectItem>
                                                        <SelectItem value="48">48 Hours</SelectItem>
                                                        <SelectItem value="72">72 Hours</SelectItem>
                                                        <SelectItem value="168">1 Week</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                    </div>
                                    <DialogFooter>
                                        <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                                        <Button onClick={handleCreateLink} disabled={creating}>
                                            {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                            Generate Link
                                        </Button>
                                    </DialogFooter>
                                </DialogContent>
                            </Dialog>

                            {/* Links List */}
                            <Card>
                                    {loading ? (
                                        <div className="flex justify-center p-12"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
                                    ) : links.length === 0 ? (
                                        <div className="text-center py-16 border-dashed border-2 rounded-xl bg-slate-50/50 dark:bg-slate-800/20">
                                            <Users className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                                            <p className="text-slate-500 font-medium">No invitation links yet.</p>
                                            <p className="text-xs text-slate-400 mt-1">Create your first link to start registration drive.</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {/* Mobile Cards */}
                                            <div className="grid grid-cols-1 gap-4 sm:hidden">
                                                {links.map((link) => (
                                                    <Card key={link.id} className="overflow-hidden border-slate-200 dark:border-slate-800 relative group">
                                                        <div className={`absolute left-0 top-0 bottom-0 w-1 ${
                                                            link.status === 'active' ? 'bg-green-500' : link.status === 'revoked' ? 'bg-red-500' : 'bg-slate-300'
                                                        }`} />
                                                        <CardContent className="p-4">
                                                            <div className="flex justify-between items-start mb-3 ml-2">
                                                                <div>
                                                                    <h3 className="font-black text-slate-900 dark:text-white uppercase tracking-tighter text-lg leading-none">{link.name}</h3>
                                                                    <div className="flex items-center gap-1.5 mt-2">
                                                                        <code className="text-[10px] bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded font-mono text-slate-600 dark:text-slate-400">{link.code}</code>
                                                                        <span className="text-slate-300 dark:text-slate-700">|</span>
                                                                        <code className="text-[10px] bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded font-mono text-slate-600 dark:text-slate-400">{link.passcode}</code>
                                                                    </div>
                                                                </div>
                                                                <DropdownMenu>
                                                                    <DropdownMenuTrigger asChild>
                                                                        <Button size="icon" variant="ghost" className="h-8 w-8 -mt-1 -mr-1">
                                                                            <MoreHorizontal className="h-4 w-4" />
                                                                        </Button>
                                                                    </DropdownMenuTrigger>
                                                                    <DropdownMenuContent align="end" className="w-48">
                                                                        <DropdownMenuItem onClick={() => copyLink(link.code)}><Copy className="h-4 w-4 mr-2" /> Link</DropdownMenuItem>
                                                                        <DropdownMenuItem onClick={() => copyPasscode(link.passcode)}><Copy className="h-4 w-4 mr-2" /> Passcode</DropdownMenuItem>
                                                                        <DropdownMenuSeparator />
                                                                        {link.status === 'active' && (
                                                                            <DropdownMenuItem onClick={() => handleRevoke(link.id)} className="text-red-500"><Ban className="h-4 w-4 mr-2" /> Revoke</DropdownMenuItem>
                                                                        )}
                                                                        <DropdownMenuItem onClick={() => handleDelete(link.id)} className="text-red-500 font-bold"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                                                                    </DropdownMenuContent>
                                                                </DropdownMenu>
                                                            </div>
                                                            
                                                            <div className="grid grid-cols-2 gap-4 ml-2 mt-4">
                                                                <div className="space-y-1">
                                                                    <p className="text-[9px] uppercase font-bold text-slate-400 tracking-wider">Usage</p>
                                                                    <div className="flex items-center gap-2">
                                                                        <div className="h-1.5 flex-1 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                                                            <div 
                                                                                className="h-full bg-orange-500 rounded-full" 
                                                                                style={{ width: `${Math.min(100, (link.usedCount / link.maxUses) * 100)}%` }}
                                                                            />
                                                                        </div>
                                                                        <span className="text-[10px] font-bold text-slate-700 dark:text-slate-300 whitespace-nowrap">{link.usedCount}/{link.maxUses}</span>
                                                                    </div>
                                                                </div>
                                                                <div className="space-y-1">
                                                                    <p className="text-[9px] uppercase font-bold text-slate-400 tracking-wider">Expiry</p>
                                                                    <p className="text-[10px] font-bold text-slate-700 dark:text-slate-300 truncate">
                                                                        {new Date(link.expiresAt).toLocaleDateString()}
                                                                    </p>
                                                                </div>
                                                            </div>

                                                            <Button 
                                                                className="w-full mt-5 bg-slate-900 dark:bg-white dark:text-slate-900 h-10 font-bold text-xs uppercase tracking-widest"
                                                                onClick={() => viewVolunteers(link)}
                                                            >
                                                                Manage Volunteers
                                                            </Button>
                                                        </CardContent>
                                                    </Card>
                                                ))}
                                            </div>

                                            {/* Desktop Table */}
                                            <div className="hidden sm:block overflow-hidden border rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50">
                                                <Table>
                                                    <TableHeader className="bg-slate-50 dark:bg-slate-800/50">
                                                        <TableRow>
                                                            <TableHead className="font-bold py-4">Link Details</TableHead>
                                                            <TableHead className="font-bold">Security</TableHead>
                                                            <TableHead className="font-bold">Status</TableHead>
                                                            <TableHead className="font-bold">Utilization</TableHead>
                                                            <TableHead className="font-bold">Expiry</TableHead>
                                                            <TableHead className="text-right font-bold pr-6">Manage</TableHead>
                                                        </TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {links.map((link) => (
                                                            <TableRow key={link.id} className="group hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors">
                                                                <TableCell className="py-4">
                                                                    <p className="font-bold text-slate-900 dark:text-slate-100">{link.name}</p>
                                                                    <p className="text-[10px] font-mono text-slate-400 uppercase mt-0.5">{link.code}</p>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <code className="text-xs bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded font-mono border border-slate-200 dark:border-slate-700">
                                                                        {link.passcode}
                                                                    </code>
                                                                </TableCell>
                                                                <TableCell>
                                                                    {link.status === 'active' && <Badge className="bg-green-500/10 text-green-600 hover:bg-green-500/10 border-green-500/20 px-2 py-0 border">Active</Badge>}
                                                                    {link.status === 'revoked' && <Badge variant="destructive">Revoked</Badge>}
                                                                    {link.status === 'expired' && <Badge variant="outline" className="text-slate-400">Expired</Badge>}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <div className="flex flex-col gap-1.5 w-24">
                                                                        <div className="flex justify-between text-[10px] font-bold">
                                                                            <span>{Math.round((link.usedCount/link.maxUses)*100)}%</span>
                                                                            <span className="text-slate-400">{link.usedCount}/{link.maxUses}</span>
                                                                        </div>
                                                                        <div className="h-1 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                                                            <div className="h-full bg-orange-500" style={{ width: `${(link.usedCount/link.maxUses)*100}%` }} />
                                                                        </div>
                                                                    </div>
                                                                </TableCell>
                                                                <TableCell className="text-xs text-slate-500">
                                                                    {new Date(link.expiresAt).toLocaleDateString()}
                                                                </TableCell>
                                                                <TableCell className="text-right pr-6">
                                                                    <div className="flex justify-end gap-1">
                                                                        <Button size="sm" variant="outline" onClick={() => viewVolunteers(link)} className="h-8 text-[11px] font-bold uppercase tracking-wider">
                                                                            Volunteers
                                                                        </Button>
                                                                        <DropdownMenu>
                                                                            <DropdownMenuTrigger asChild>
                                                                                <Button size="icon" variant="ghost" className="h-8 w-8">
                                                                                    <MoreHorizontal className="h-4 w-4" />
                                                                                </Button>
                                                                            </DropdownMenuTrigger>
                                                                            <DropdownMenuContent align="end">
                                                                                <DropdownMenuItem onClick={() => copyLink(link.code)}><Copy className="h-4 w-4 mr-2" /> Link</DropdownMenuItem>
                                                                                <DropdownMenuItem onClick={() => copyPasscode(link.passcode)}><Copy className="h-4 w-4 mr-2" /> Passcode</DropdownMenuItem>
                                                                                <DropdownMenuSeparator />
                                                                                {link.status === 'active' && (
                                                                                    <DropdownMenuItem onClick={() => handleRevoke(link.id)} className="text-red-500"><Ban className="h-4 w-4 mr-2" /> Revoke</DropdownMenuItem>
                                                                                )}
                                                                                <DropdownMenuItem onClick={() => handleDelete(link.id)} className="text-red-500"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                                                                            </DropdownMenuContent>
                                                                        </DropdownMenu>
                                                                    </div>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </div>
                                        </div>
                                    )}
                            </Card>
                         </div>
                    </main>
                </div>
            </div>
        </SidebarProvider>
    );
};

export default RegistrationManagement;
